import Konva from "konva";
import React from "react";
import { useConstructionAnimation } from "./ConstructionAnimationContext";
import {
  CONSTRUCTION_DURATION,
  CONSTRUCTION_LIFT,
  constructionTiming
} from "./constructionTiming";

export function useKonvaConstructionAnimation(
  stageRef: React.RefObject<Konva.Stage | null>
) {
  const { register } = useConstructionAnimation();
  React.useEffect(
    () =>
      register((map) => {
        const stage = stageRef.current;
        if (!stage) return () => {};
        const shapes = new Map(
          map.shapes
            .filter(
              (shape) =>
                !shape.hidden &&
                shape.layerType !== "background" &&
                shape.layerType !== "floor"
            )
            .map((shape) => [`shape-${shape.id}`, shape])
        );
        const rank = (type: string) =>
          type === "wall-path" || type === "wall"
            ? 0
            : type === "text" || type === "spot"
              ? 2
              : 1;
        const nodes = stage
          .find((node: Konva.Node) => shapes.has(node.id()))
          .map((node) => ({
            node,
            y: node.y(),
            opacity: node.opacity(),
            shape: shapes.get(node.id())!
          }))
          .sort(
            (a, b) =>
              rank(a.shape.type) - rank(b.shape.type) || a.shape.y - b.shape.y
          );
        const timing = constructionTiming(nodes.length);
        const entries = nodes.map((entry, index) => ({
          ...entry,
          distance: CONSTRUCTION_LIFT,
          delay: timing.delay(index)
        }));
        for (const entry of entries) {
          entry.node.y(entry.y + entry.distance);
          entry.node.opacity(0);
        }
        const animation = new Konva.Animation((frame) => {
          if (!frame) return;
          for (const entry of entries) {
            const progress = Math.min(
              1,
              Math.max(0, (frame.time - entry.delay) / CONSTRUCTION_DURATION)
            );
            const eased = 1 - (1 - progress) ** 4;
            entry.node.y(entry.y + entry.distance * (1 - eased));
            entry.node.opacity(entry.opacity * eased);
          }
          if (frame.time >= timing.end) animation.stop();
        }, stage.getLayers());
        animation.start();
        return () => {
          animation.stop();
          for (const entry of entries) {
            if (!entry.node.getStage()) continue;
            entry.node.y(entry.y);
            entry.node.opacity(entry.opacity);
          }
          stage.batchDraw();
        };
      }),
    [register, stageRef]
  );
}
